import LanguagePreferences from '@/components/LanguagePreferences'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { useCurrentUser } from '@/hooks/useCurrentUser'
import { useNotificationPreferencesAccess } from '@/hooks/useNotificationPreferencesAccess'
import { useOwnCoachPhoto } from '@/hooks/useOwnCoachPhoto'
import { useUserButtonAppearance } from '@/hooks/useUserButtonAppearance'
import { SignOutButton, UserButton, useUser } from '@clerk/clerk-react'
import { useFlag } from '@unleash/proxy-client-react'
import { Bolt, Bug, ClipboardList, Languages, LogOut, Megaphone, Settings, Sparkles, UserCheck } from 'lucide-react'
import { useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'
import { Userpilot } from 'userpilot'

type Props = {
  readonly hasRole: boolean
  readonly isEngenStaff: boolean
  readonly hasAdminAccess: boolean
  /** Where the *Admin Portal* entry goes. */
  readonly adminPath: string
  /** Whether to offer the *Coaching Portal* entry; `useCoachingPortalEntry` decides. Absent rather than disabled for everybody else. */
  readonly hasCoachingPortalAccess: boolean
  /** Where the *Coaching Portal* entry goes; `useCoachingPortalEntry` holds it. */
  readonly coachingPortalPath: string
  readonly canSeeChangelog: boolean
  readonly onNeedsAnalysisOpen: () => void
  readonly onFeedbackOpen: () => void
  readonly onReportBugOpen: () => void
  readonly onChangelogOpen: () => void
  readonly changelogUnreadCount: number
  readonly onManageAccountOpen: () => void
}

export function UserButtonMenu({
  hasRole,
  isEngenStaff,
  hasAdminAccess,
  adminPath,
  hasCoachingPortalAccess,
  coachingPortalPath,
  canSeeChangelog,
  onNeedsAnalysisOpen,
  onFeedbackOpen,
  onReportBugOpen,
  onChangelogOpen,
  changelogUnreadCount,
  onManageAccountOpen,
}: Props) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { user: clerkUser } = useUser()
  const { user: dbUser } = useCurrentUser()
  const useNewManageAccount = useFlag('new-manage-account')
  const showNotificationPreferences = useNotificationPreferencesAccess()
  const useCustomAccountMenu = useNewManageAccount || showNotificationPreferences
  const appearance = useUserButtonAppearance()
  const coachPhoto = useOwnCoachPhoto()
  const triggerRef = useRef<HTMLButtonElement>(null)
  const pendingDialogRef = useRef<(() => void) | null>(null)

  // --- Legacy Clerk UserButton (flag OFF) ---
  if (!useCustomAccountMenu) {
    return (
      <UserButton appearance={appearance}>
        <UserButton.MenuItems>
          {hasRole && (
            <UserButton.Action
              label={t('userMenu.needsAnalysis')}
              labelIcon={<ClipboardList size={16} aria-hidden='true' />}
              onClick={() => {
                Userpilot.track('Needs Analysis Clicked')
                onNeedsAnalysisOpen()
              }}
            />
          )}
          {isEngenStaff && (
            <UserButton.Action
              label={t('userMenu.giveProductFeedback')}
              labelIcon={<Megaphone size={16} aria-hidden='true' />}
              onClick={() => {
                Userpilot.track('Give Product Feedback Clicked')
                onFeedbackOpen()
              }}
            />
          )}
          {isEngenStaff && (
            <UserButton.Action
              label={t('userMenu.reportBug')}
              labelIcon={<Bug size={16} aria-hidden='true' />}
              onClick={() => {
                Userpilot.track('Report Bug Clicked')
                onReportBugOpen()
              }}
            />
          )}
          {canSeeChangelog && (
            <UserButton.Action
              label={
                changelogUnreadCount > 0
                  ? `${t('userMenu.whatsNew')} (${changelogUnreadCount})`
                  : t('userMenu.whatsNew')
              }
              labelIcon={<Sparkles size={16} aria-hidden='true' />}
              onClick={() => {
                Userpilot.track("What's New Clicked")
                onChangelogOpen()
              }}
            />
          )}
          {hasAdminAccess && (
            <UserButton.Action
              label={t('userMenu.adminPortal')}
              labelIcon={<Bolt size={16} aria-hidden='true' />}
              onClick={() => navigate(adminPath)}
            />
          )}
          {/* Beside Admin Portal, because this menu is the portal switcher and the
              coach workspace is a portal (ENG-3568). A super admin sees both. */}
          {hasCoachingPortalAccess && (
            <UserButton.Action
              label='Coaching Portal'
              labelIcon={<UserCheck size={16} aria-hidden='true' />}
              onClick={() => navigate(coachingPortalPath)}
            />
          )}
        </UserButton.MenuItems>
        <UserButton.UserProfilePage
          label={t('userMenu.language')}
          url='language'
          labelIcon={<Languages size={16} aria-hidden='true' />}
        >
          <LanguagePreferences />
        </UserButton.UserProfilePage>
      </UserButton>
    )
  }

  // --- Custom dropdown menu (flag ON) ---
  const initials = clerkUser?.fullName
    ?.split(' ')
    .map((n) => n[0])
    .join('')
    .slice(0, 2)
    .toUpperCase()

  const displayEmail = dbUser?.email || clerkUser?.primaryEmailAddress?.emailAddress

  const itemClass = 'cursor-pointer gap-3 px-3 py-2.5 rounded-lg focus:bg-accent-purple/10 focus:text-accent-foreground'
  const iconClass = 'size-4 text-muted-foreground'

  // Open dialogs only once the menu has closed and handed focus back to the trigger,
  // so the dialog takes focus from there and returns it there on close.
  const openAfterClose = (open: () => void) => {
    pendingDialogRef.current = open
  }

  const handleCloseAutoFocus = (event: Event) => {
    const open = pendingDialogRef.current
    pendingDialogRef.current = null
    if (!open) return
    event.preventDefault()
    triggerRef.current?.focus()
    open()
  }

  return (
    <DropdownMenu
      // A reopen during the exit animation keeps the content mounted, so the close callback never fires.
      onOpenChange={(open) => {
        if (open) pendingDialogRef.current = null
      }}
    >
      <DropdownMenuTrigger asChild>
        <button
          ref={triggerRef}
          type='button'
          aria-label={t('userMenu.userMenuLabel')}
          className='rounded-full transition-opacity hover:opacity-80'
        >
          <Avatar className='size-9'>
            <AvatarImage src={coachPhoto ?? clerkUser?.imageUrl} alt={clerkUser?.fullName || ''} />
            <AvatarFallback className='bg-slate-200 text-xs font-medium text-slate-600'>{initials}</AvatarFallback>
          </Avatar>
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align='end' className='w-72 rounded-xl shadow-lg' onCloseAutoFocus={handleCloseAutoFocus}>
        <div className='flex items-center gap-3 px-3 py-3'>
          <Avatar className='size-10 ring-2 ring-accent-purple/20'>
            <AvatarImage src={coachPhoto ?? clerkUser?.imageUrl} alt={clerkUser?.fullName || ''} />
            <AvatarFallback className='bg-accent-purple/10 text-sm font-medium text-accent-purple'>
              {initials}
            </AvatarFallback>
          </Avatar>
          <div className='flex flex-col'>
            <span className='text-sm font-semibold text-foreground'>{clerkUser?.fullName}</span>
            {displayEmail && <span className='text-xs text-muted-foreground'>{displayEmail}</span>}
          </div>
        </div>
        <DropdownMenuSeparator />
        <DropdownMenuGroup>
          <DropdownMenuItem className={itemClass} onClick={() => openAfterClose(onManageAccountOpen)}>
            <Settings className={iconClass} />
            <span>{t('accountDialog.manageAccount')}</span>
          </DropdownMenuItem>
          {hasRole && (
            <DropdownMenuItem
              className={itemClass}
              onClick={() => {
                Userpilot.track('Needs Analysis Clicked')
                openAfterClose(onNeedsAnalysisOpen)
              }}
            >
              <ClipboardList className={iconClass} />
              <span>{t('userMenu.needsAnalysis')}</span>
            </DropdownMenuItem>
          )}
          {isEngenStaff && (
            <DropdownMenuItem
              className={itemClass}
              onClick={() => {
                Userpilot.track('Give Product Feedback Clicked')
                openAfterClose(onFeedbackOpen)
              }}
            >
              <Megaphone className={iconClass} />
              <span>{t('userMenu.giveProductFeedback')}</span>
            </DropdownMenuItem>
          )}
          {isEngenStaff && (
            <DropdownMenuItem
              className={itemClass}
              onClick={() => {
                Userpilot.track('Report Bug Clicked')
                openAfterClose(onReportBugOpen)
              }}
            >
              <Bug className={iconClass} />
              <span>{t('userMenu.reportBug')}</span>
            </DropdownMenuItem>
          )}
          {canSeeChangelog && (
            <DropdownMenuItem
              className={itemClass}
              onClick={() => {
                Userpilot.track("What's New Clicked")
                openAfterClose(onChangelogOpen)
              }}
            >
              <Sparkles className={iconClass} />
              <span className='flex-1'>{t('userMenu.whatsNew')}</span>
              {changelogUnreadCount > 0 && (
                <span className='ml-auto inline-flex h-5 min-w-[20px] items-center justify-center rounded-full bg-accent-purple px-1.5 text-xs font-semibold text-white'>
                  {changelogUnreadCount > 9 ? '9+' : changelogUnreadCount}
                </span>
              )}
            </DropdownMenuItem>
          )}
          {hasAdminAccess && (
            <DropdownMenuItem className={itemClass} onClick={() => navigate(adminPath)}>
              <Bolt className={iconClass} />
              <span>{t('userMenu.adminPortal')}</span>
            </DropdownMenuItem>
          )}
          {/* Beside Admin Portal, because this menu is the portal switcher and the
                coach workspace is a portal (ENG-3568). A super admin sees both. */}
          {hasCoachingPortalAccess && (
            <DropdownMenuItem className={itemClass} onClick={() => navigate(coachingPortalPath)}>
              <UserCheck className={iconClass} />
              <span>Coaching Portal</span>
            </DropdownMenuItem>
          )}
        </DropdownMenuGroup>
        <DropdownMenuSeparator />
        <SignOutButton>
          <DropdownMenuItem className={`${itemClass} text-muted-foreground`}>
            <LogOut className='size-4' />
            <span>{t('userMenu.signOut')}</span>
          </DropdownMenuItem>
        </SignOutButton>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
